import { useState } from "react";
const primaryStyles = {
  backgroundColor: "#5B9BD5",
  color: "#FFFFFF",
  borderWidth: 0,
  borderStyle: "solid" as const,
  borderColor: "transparent",
};

// Tinted, not white. A white box with a thin grey border is exactly what an
// input looks like, so on a filter bar you could not tell which things you
// type into and which you click. The tint is the brand blue at its lightest,
// the same family as the focus ring.
const secondaryStyles = {
  backgroundColor: "#EDF3F9",
  color: "#1E2128FF",
  borderWidth: 1,
  borderStyle: "solid" as const,
  borderColor: "#C7DCEC",
};

const tertiaryStyles = {
  backgroundColor: "#FFFFFF",
  color: "#8e7248",
  borderWidth: 1.5,
  borderStyle: "solid" as const,
  borderColor: "#8e7248",
};

const dangerStyles = {
  backgroundColor: "#e57373",
  color: "#FFFFFF",
  borderWidth: 0,
  borderStyle: "solid" as const,
  borderColor: "transparent",
};

const dangerOutlineStyles = {
  backgroundColor: "#FEF2F2",
  color: "#dc2626",
  borderWidth: 1,
  borderStyle: "solid" as const,
  borderColor: "#FECACA",
};

const themedStyles = {
  backgroundColor: "var(--accent)" as string,
  color: "var(--accent-ink)" as string,
  borderWidth: 0,
  borderStyle: "solid" as const,
  borderColor: "transparent",
};

const Button = (props: {
  variant:
    | "primary"
    | "secondary"
    | "tertiary"
    | "danger"
    | "dangerOutline"
    | "themed";
  size?: "default" | "small";
  style?: React.CSSProperties;
  children?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  icon?: React.JSX.Element;
  "data-testid"?: string;
  type?: "submit";
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getBaseStyles = () => {
    switch (props.variant) {
      case "primary":
        return primaryStyles;
      case "secondary":
        return secondaryStyles;
      case "tertiary":
        return tertiaryStyles;
      case "danger":
        return dangerStyles;
      case "dangerOutline":
        return dangerOutlineStyles;
      case "themed":
        return themedStyles;
      default:
        return primaryStyles;
    }
  };

  const getHoverStyles = () => {
    if (!isHovered || props.disabled) return {};

    if (props.variant === "primary") {
      return { backgroundColor: "#3A7DB8" };
    } else if (props.variant === "secondary") {
      return { backgroundColor: "#F8F5F0" };
    } else if (props.variant === "tertiary") {
      return { backgroundColor: "#FAF8F5" };
    } else if (props.variant === "danger") {
      return { backgroundColor: "#d32f2f" };
    } else if (props.variant === "dangerOutline") {
      return { backgroundColor: "#FEE2E2", borderColor: "#FCA5A5" };
    } else if (props.variant === "themed") {
      return { opacity: 0.88 };
    }
  };

  const getSizeStyles = () => {
    if (props.size === "small") {
      return {
        padding: "8px 16px",
        // Same radius as a full-size button — only the padding and type size
        // make it small. It used to be 4, so the two sat side by side with
        // visibly different corners.
        borderRadius: 8,
        fontSize: 14,
      };
    }
    return {
      padding: "12px 24px",
      borderRadius: 8,
      fontSize: 15,
    };
  };

  return (
    <button
      style={{
        ...getBaseStyles(),
        ...getSizeStyles(),
        cursor: props.disabled ? "not-allowed" : "pointer",
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Inter, -apple-system, sans-serif",
        transition: "all 0.2s ease",
        whiteSpace: "nowrap",
        ...(props.disabled && {
          backgroundColor: "#95A5A6",
          color: "#FFFFFF",
          borderWidth: 0,
          borderColor: "transparent",
          opacity: 0.6,
        }),
        ...getHoverStyles(),
        ...props.style,
      }}
      type={props.type}
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props["data-testid"]}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {props.icon && (
        <span
          style={{
            marginRight: 8,
            height: 20,
            display: "flex",
            alignItems: "center",
          }}
        >
          {props.icon}
        </span>
      )}
      {props.children}
    </button>
  );
};

export default Button;
